<script setup>
import {onMounted, ref, computed} from 'vue';
import {useRouter, useRoute} from 'vue-router';
import {definePage} from "unplugin-vue-router/runtime";
import logoWhite from "@/assets/logo_full_white.svg"

import createApiService from '@/services/api';
import eyeIcon from "@/assets/eye.svg";
import eyeSlashIcon from "@/assets/eye-slash.svg";

const router = useRouter();
const route = useRoute();
const api = createApiService(router);

definePage({
  params: ['hash', 'userId'],
  meta: {
    title: 'Wachtwoord instellen',
    requiresAuth: false,
  },
})

import {useMessageStore} from "@/stores/message.js";
const messageStore = useMessageStore();

const loading = ref(false);
const password = ref('');
const showPassword = ref(false);
const passwordRepeat = ref('');
const showPasswordRepeat = ref(false);

const message = ref('');
const errors = ref([]);
const initialLoadComplete = ref(false);

const checkHash = async () => {
  try {
    const response = await api.post('passwordreset', {hash: route.params.hash, userId: route.params.userId});

    if (response.status !== 200) {
      messageStore.addMessage('Er is iets misgegaan. Probeer het later nog eens.', 'error');
      await router.push('/inloggen')
    }
  } catch (e) {
    let message = e.response?.status === 400 ? [e.response.data.message] : ['Er is iets misgegaan. Probeer het later nog eens.'];
    messageStore.addMessage(message, 'error');
    await router.push('/inloggen')
  } finally {
    initialLoadComplete.value = true;
  }
}

const requestPassword = async () => {
  loading.value = true;
  resetMessages();

  try {
    const response = await api.post('passwordreset', {hash: route.params.hash, userId: route.params.userId, password: password.value, passwordRepeat: passwordRepeat.value});

    if (response.status === 200) {
      messageStore.addMessage(response.data.message);
      await router.push('/inloggen')
    }
    else {
      errors.value = ['Er is iets misgegaan. Probeer het later nog eens.'];
    }
  } catch (e) {
    let message = e.response?.status === 400 ? [e.response.data.message] : ['Er is iets misgegaan. Probeer het later nog eens.'];
    errors.value = [message];
  } finally {
    loading.value = false;
  }
}

const resetMessages = () => {
  message.value = "";
  errors.value = [];
}

onMounted(async () => {
  await checkHash();
});
</script>

<template>
  <the-loader v-if="loading"/>

  <v-card
    class="ma-auto pa-5"
    color="primary"
    :disabled="loading"
    max-width="500"
  >

    <v-img :src="logoWhite" max-width="50%" class="mx-auto mb-12"></v-img>

    <v-form
      v-if="initialLoadComplete"
      @submit.prevent="requestPassword"
    >
      <v-card-text class="pl-0 pr-0">

        <v-alert
          v-if="errors.length"
          class="mx-auto mb-5"
          max-width="500"
          color="error"
          variant="elevated"
        >
          <template #text>
            <span
              v-for="(error, index) in errors"
              :key="index"
            >
              {{ error }}
            </span>
          </template>
        </v-alert>

        <div class="mb-4">
          <h4 class="mb-2">Wachtwoord <span class="text-error">*</span></h4>
          <v-text-field
            v-model="password"
            placeholder="Wachtwoord"
            variant="solo"
            :type="showPassword ? 'text' : 'password'"
            hide-details
          >
            <template #append-inner>
              <v-icon
                :title="showPassword ? 'Verbergen' : 'Tonen'"
                @click="showPassword = !showPassword"
                class="cursor-pointer opacity-100"
              >
                <v-img :src="showPassword ? eyeIcon : eyeSlashIcon"></v-img>
              </v-icon>
            </template>
          </v-text-field>
        </div>

        <div class="mb-4">
          <h4 class="mb-2">Herhaal wachtwoord <span class="text-error">*</span></h4>
          <v-text-field
            v-model="passwordRepeat"
            placeholder="Wachtwoord"
            variant="solo"
            :type="showPasswordRepeat ? 'text' : 'password'"
            hide-details
          >
            <template #append-inner>
              <v-icon
                :title="showPasswordRepeat ? 'Verbergen' : 'Tonen'"
                @click="showPasswordRepeat = !showPasswordRepeat"
                class="cursor-pointer opacity-100"
              >
                <v-img :src="showPasswordRepeat ? eyeIcon : eyeSlashIcon"></v-img>
              </v-icon>
            </template>
          </v-text-field>
        </div>

      </v-card-text>

      <v-card-actions class="flex-column ma-auto pa-0 mt-2 actions">
        <v-btn
          class="mb-2 w-100"
          height="54"
          type="submit"
          color="secondary"
          variant="elevated"
          block
        >
          <h4>Wachtwoord opslaan</h4>
        </v-btn>
      </v-card-actions>
    </v-form>

    <div
      v-else
      class="text-center py-10"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>

    <div class="bottom-link text-center w-100 pb-4 mt-8">
      <a href="/inloggen" class="text-white">
        Terug naar inloggen
      </a>
    </div>
  </v-card>


</template>

<style scoped>

.actions {
  min-height: auto;
}

</style>
